import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Chip, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { capitalizeFirstLetter, FieldsStatus, VerificationStatus } from 'api/file/interfaces/get-file-order-insights';
import clsx from 'clsx';
import ExpandableInfoPanel from 'ui-kit/components/accordion/ExpandableInfoPanel';
import MaskedValue from './MaskedValue';
import OrderInsightsRiskIcon from './OrderInsightsRiskIcon';

type StatusRowProps = {
  label: string;
  value: VerificationStatus | boolean | string | undefined;
  index: number;
  isPlainText?: boolean;
  interpretBooleanAsText?: boolean;
  isPdf?: boolean;
  mode?: string;
  tooltipText?: string;
  masked?: boolean;
  fullyMasked?: boolean;
  dataTestId?: string;
  moreInfoTestId?: string;
};

export const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 2),
    },
    rowWithTooltip: {
      alignItems: 'flex-start',
    },
    striped: {
      backgroundColor: theme.palette.brand.feather50,
    },
    pdfLabel: {
      fontWeight: 500,
      width: '27%',
      maxWidth: '157px',
      minWidth: '157px',
    },
    pdfValue: {
      width: 'calc(90% - 145px)',
      display: 'flex',
      justifyContent: 'flex-start',
    },
    label: {
      width: '25%',
      minWidth: '250px',
      maxWidth: '250px',
    },
    value: {
      width: '70%',
      display: 'flex',
      justifyContent: 'flex-start',
      marginLeft: theme.spacing(2),
    },
    chipPdfBase: {
      fontWeight: 400,
      fontSize: pxToRem(12),
      lineHeight: pxToRem(18),
      letterSpacing: '0.16px',
      textAlign: 'center',
      verticalAlign: 'middle',
      paddingLeft: theme.spacing(1.25),
      '& .MuiChip-label': {
        paddingLeft: theme.spacing(0.5),
      },
    },
    chipBase: {
      display: 'flex',
      height: '20px',
      padding: `0 ${theme.spacing(0.75)}`,
      alignItems: 'center',
      gap: '4px',
      fontSize: pxToRem(12),
      verticalAlign: 'middle',
      '& .MuiChip-label': {
        paddingLeft: 0,
        paddingRight: 0,
      },
      '& .MuiSvgIcon-root': {
        width: '16px',
        height: '16px',
      },
    },
    customChipHeight: {
      height: '24px',
    },
    passed: {
      backgroundColor: '#E8F5E9',
      color: theme.palette.success.dark,
    },
    failed: {
      backgroundColor: '#FDECEA',
      color: theme.palette.error.light,
    },
    warning: {
      backgroundColor: theme.palette.agGrid.overrideBackground,
      color: theme.palette.warning.dark,
    },
    pdfDefault: {
      backgroundColor: '#e0e0e0',
      color: '#000',
    },
    default: {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.text.secondary,
    },
    partyChips: {
      gap: 0,
    },
    limitReached: {
      backgroundColor: '#FBE9E7',
      color: theme.palette.accordion.seller,
    },
  }),
);

export const getChipProps = (value: string, classes: Record<string, string>, isPdf: boolean, isPartyChip?: boolean) => {
  const normalizedValue = [FieldsStatus.USER_CANCELLED, FieldsStatus.REQUEST_CANCELLED]
    .map((status) => status.toLowerCase())
    .includes(value.toLowerCase())
    ? FieldsStatus.REQUEST_CANCELLED
    : value;

  switch (normalizedValue) {
    case FieldsStatus.PASSED:
    case FieldsStatus.SUCCESS:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${classes.passed} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Success" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.FAILED:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${classes.failed} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Alert" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.WARNING:
    case FieldsStatus.ISSUES_FOUND:
    case FieldsStatus.ERROR:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${classes.warning} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Warning" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.PENDING:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${classes.warning} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Pending" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.EXPIRED:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${isPdf ? classes.pdfDefault : classes.default} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Expired" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.REQUEST_CANCELLED:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${isPdf ? classes.pdfDefault : classes.default} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="Error" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
    case FieldsStatus.LIMIT_REACHED:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${classes.limitReached} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: (
          <OrderInsightsRiskIcon riskSeverity={FieldsStatus.LIMIT_REACHED} isPdf={isPdf} isPartyChip={isPartyChip} />
        ),
      };
    default:
      return {
        className: `${isPdf ? classes.chipPdfBase : classes.chipBase} ${isPdf ? classes.pdfDefault : classes.default} ${
          isPdf ? classes.customChipHeight : ''
        }`,
        icon: <OrderInsightsRiskIcon riskSeverity="None" isPdf={isPdf} isPartyChip={isPartyChip} />,
      };
  }
};

const StatusRow: React.FC<StatusRowProps> = ({
  label,
  value,
  index,
  mode,
  isPlainText = false,
  interpretBooleanAsText = false,
  isPdf = false,
  tooltipText,
  masked = false,
  fullyMasked = false,
  dataTestId,
  moreInfoTestId,
}: StatusRowProps) => {
  const classes = useStyles();
  const isEvenRow = index % 2 === 0;
  let displayValue: string = FieldsStatus.NOT_APPLICABLE;

  if (typeof value === 'boolean') {
    displayValue = interpretBooleanAsText ? (value ? 'Yes' : 'No') : `${value}`;
  } else if (typeof value === 'string') {
    displayValue = value.trim() || FieldsStatus.NOT_APPLICABLE;
  }
  const { className, icon } = getChipProps(mode ? mode : displayValue, classes, isPdf);
  const chipLabel =
    displayValue === FieldsStatus.NOT_APPLICABLE ? FieldsStatus.NOT_APPLICABLE : capitalizeFirstLetter(displayValue);

  const containerClass = clsx(
    classes.row,
    isEvenRow ? classes.striped : '',
    isPdf ? 'defaultPageBreak' : '',
    tooltipText && !isPdf ? classes.rowWithTooltip : '',
  );

  const renderValue = () => {
    if (masked && isPlainText && displayValue !== FieldsStatus.NOT_APPLICABLE) {
      return <MaskedValue value={displayValue} dataTestId={dataTestId} fullyMasked={fullyMasked} />;
    }
    if (isPlainText) {
      return <Typography>{displayValue}</Typography>;
    }
    return <Chip label={chipLabel} className={className} icon={icon} />;
  };

  return (
    <Box className={containerClass}>
      <Typography className={isPdf ? classes.pdfLabel : classes.label}>{label}</Typography>
      <Box className={isPdf ? classes.pdfValue : classes.value}>
        <ExpandableInfoPanel tooltipText={!isPdf ? tooltipText : undefined} moreInfoTestId={moreInfoTestId}>
          {renderValue()}
        </ExpandableInfoPanel>
      </Box>
    </Box>
  );
};

export default StatusRow;
